import { RdcTransaction } from '@varo/graphql-schema-types';
import { Href, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import theme from 'shared/styles/theme';
import { formatDate } from 'shared/utils/string/date';

import TransactionItems from './TransactionItems';

interface RdcTransactionDetailsProps {
  transaction: RdcTransaction;
}

const RdcTransactionDetails = ({ transaction }: RdcTransactionDetailsProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'transactionDetails.rdcDetails',
  });

  const goToAgreements = () => {
    const productType = transaction?.accountLink?.product;
    let url = '/myvaro/legal';
    if (productType) {
      url = `${url}?agreementTypes=${productType}`;
    }
    router.navigate(url as Href);
  };

  const detailsR2 = [
    {
      items: [
        ...(transaction?.status
          ? [
              {
                value: transaction.status,
                title: translatedText('status'),
                key: 'rdc-status',
              },
            ]
          : []),
        ...(transaction?.createdAt
          ? [
              {
                value: formatDate(transaction.createdAt, 'MMM d, yyyy'),
                title: translatedText('createdAt'),
                key: 'rdc-createdAt',
              },
            ]
          : []),
        ...(transaction?.confirmationNumber
          ? [
              {
                value: transaction.confirmationNumber,
                title: translatedText('confirmationNumber'),
                key: 'rdc-confirmationNumber',
              },
            ]
          : []),
      ],
    },
    {
      items: [
        ...(transaction?.accountLink?.nickname
          ? [
              {
                value: transaction.accountLink.nickname,
                title: translatedText('depositedTo'),
                key: 'rdc-depositTo',
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <View>
      <TransactionItems items={detailsR2} type={transaction.type} />
      {transaction?.checkImages ? <View style={styles.divider} /> : null}
      {transaction?.checkImages?.map((image) => (
        <Image
          source={{ uri: image.url }}
          alt={image.title}
          key={image.url}
          style={styles.checkImage}
          testID={image.url}
        />
      ))}

      {transaction?.disclaimer ? (
        <BodyBaseText style={styles.disclaimerWrapper}>
          <HTMLRenderer htmlAsString={transaction.disclaimer} />
        </BodyBaseText>
      ) : null}

      <BodySmText style={styles.footerNote}>
        {translatedText('rdcFooterNote.text') + ' '}
        <HyperlinkSmText onPress={goToAgreements}>
          {translatedText('rdcFooterNote.link.label')}
        </HyperlinkSmText>
      </BodySmText>
    </View>
  );
};

const styles = StyleSheet.create({
  checkImage: {
    height: 280,
    marginTop: theme.spacing.m,
    resizeMode: 'contain',
    width: '100%',
  },
  disclaimerWrapper: {
    padding: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  footerNote: {
    justifyContent: 'center',
    marginHorizontal: 'auto',
    marginVertical: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
  },
});

export default RdcTransactionDetails;
